import { tv } from 'tailwind-variants';

import { fontStyles } from './typography';

export const buttons = tv({
  base: [
    'inline-flex',
    'items-center',
    'justify-center',
    'content-center',
    'border-none',
    'ease-in-out',
    'duration-[200ms]',
    'disabled:transform-none',
    'disabled:cursor-not-allowed',
    'active:outline-none',
    'active:scale-[0.98]',
  ],
  variants: {
    size: {
      extraSmall: ['px-[7px]', 'h-6', fontStyles({ variant: 'LABEL_XS' })],
      small: ['px-3', 'h-9', 'w-fit', fontStyles({ variant: 'LABEL_MD' })],
      medium: ['px-4', 'h-10', fontStyles({ variant: 'LABEL_MD' })],
      large: ['h-13', 'w-full', fontStyles({ variant: 'LABEL_LG' })],
    },
    shape: {
      roundedSubtle: ['rounded-xs'],
      rounded: ['rounded-sm'],
      pill: ['rounded-2xl'],
      circle: ['rounded-full'],
    },
    iconLeft: { true: null },
    iconRight: { true: null },
    iconOnly: { true: null },
    color: {
      transparent: [
        'bg-transparent',
        'hover:bg-token-alphalight-200',
        'active:bg-token-alphalight-300',
        'focus-visible:outline-token-gray-500',
        'focus-visible:outline-[3px]',
        'disabled:text-token-gray-700',
      ],
      alphaGray: [
        'text-token-gray-1600',
        'bg-token-alphalight-200',
        'hover:bg-token-alphalight-300',
        'active:bg-token-alphalight-400',
        'outline-token-alphalight-500',
        'focus-visible:outline-token-alphalight-500',
        'focus-visible:outline-[3px]',
        'disabled:opacity-50',
      ],
      gray: [
        'bg-token-alphalight-200',
        'hover:bg-token-alphalight-300',
        'active:bg-token-alphalight-400',
        'focus-visible:outline-token-gray-500',
        'focus-visible:outline-[3px]',
        'disabled:text-token-gray-700',
      ],
      purple: [
        'bg-special-background-button-purple',
        'text-special-content-button-purple',
        'hover:bg-special-background-button-purple-hover',
        'active:bg-special-background-button-purple-pressed',
        'focus-visible:outline-token-purple-600',
        'focus-visible:outline-[3px]',
        'disabled:text-token-purple-600',
        'shadow-15',
        'disabled:shadow-none',
      ],
      red: [
        'bg-special-background-button-red-primary',
        'text-primitive-basic-white',
        'hover:bg-special-background-button-red-primary-hover',
        'active:bg-special-background-button-red-primary-pressed',
        'focus-visible:outline-token-red-600',
        'focus-visible:outline-[3px]',
        'disabled:text-token-red-600',
      ],
      black: [
        'bg-token-gray-1600',
        'text-special-content-button-primary-black',
        'hover:bg-token-gray-1200',
        'active:bg-token-gray-1600',
        'disabled:bg-token-gray-300',
        'disabled:text-token-gray-700',
        'shadow-15',
        'disabled:shadow-none',
      ],
      lightPurple: [
        'bg-token-purple-300',
        'shadow-purple',
        'text-token-purple-1100',
        'disabled:text-primitive-purple-900',
      ],
      mediumPurple: [
        'bg-token-purple-400',
        'shadow-purple',
        'text-token-purple-1100',
        'disabled:text-primitive-purple-900',
      ],
      subtlePurple: [
        'bg-token-purple-300',
        'text-token-purple-1100',
        'hover:shadow-purple',
        'active:shadow-purple',
        'disabled:text-primitive-purple-900',
      ],
      lightRed: [
        'bg-special-background-button-red-secondary',
        'text-token-red-1000',
        'hover:bg-special-background-button-red-secondary-hover',
        'active:bg-special-background-button-red-secondary-pressed',
        'disabled:text-special-content-button-red-secondary-disabled',
        'focus-visible:outline-special-border-button-red-secondary-focus',
        'focus-visible:outline-[3px]',
      ],
      white: [
        'bg-special-background-button-medium-secondary',
        'hover:bg-special-background-button-medium-secondary-hover',
        'active:bg-special-background-button-medium-secondary-pressed',
        'shadow-05',
        'disabled:text-token-gray-700',
        'focus-visible:outline-[3px]',
        'focus-visible:outline-token-alphalight-500',
      ],
    },
  },
  compoundVariants: [
    {
      size: 'small',
      iconLeft: true,
      class: 'pl-2.5 gap-1.5',
    },
    {
      size: 'small',
      iconRight: true,
      class: 'pr-2.5 gap-1.5',
    },
    {
      size: 'small',
      iconOnly: true,
      class: 'p-2.5',
    },
    {
      size: 'medium',
      iconLeft: true,
      class: 'pl-3.5 gap-2',
    },
    {
      size: 'medium',
      iconRight: true,
      class: 'pr-3.5 gap-2',
    },
    {
      size: 'medium',
      iconOnly: true,
      class: 'p-3.5',
    },
  ],
});

// Legacy variant mappings for backwards compatibility
export const btnStyles = {
  PRIMARY_LG: buttons({ size: 'large', color: 'black', shape: 'pill' }),
  PRIMARY_SM: buttons({ size: 'small', color: 'purple', shape: 'pill' }),
  PRIMARY_MD: buttons({ size: 'medium', color: 'black', shape: 'pill' }),
  PRIMARY_DESTRUCTIVE_SM: buttons({ size: 'small', color: 'red', shape: 'pill' }),
  PRIMARY_SELECT: buttons({ size: 'small', color: 'purple', shape: 'pill', iconRight: true }),
  SECONDARY_SM: buttons({ size: 'small', color: 'gray', shape: 'pill' }),
  SECONDARY_MD: buttons({ size: 'medium', color: 'white', shape: 'pill' }),
  SECONDARY_LG: buttons({ size: 'large', color: 'gray', shape: 'pill' }),
  SECONDARY_SM_DESTRUCTIVE: buttons({ size: 'small', color: 'lightRed', shape: 'pill' }),
  SECONDARY_SM_PILL: buttons({ size: 'small', color: 'white', shape: 'pill' }),
  SECONDARY_SM_ICON_ONLY: buttons({
    size: 'small',
    color: 'gray',
    shape: 'rounded',
    iconOnly: true,
  }),
  SECONDARY_SM_ICON_ONLY_TRANSPARENT: buttons({
    size: 'small',
    color: 'alphaGray',
    shape: 'rounded',
    iconOnly: true,
  }),
  SECONDARY_XS_ICON_ONLY: buttons({
    size: 'extraSmall',
    color: 'gray',
    shape: 'rounded',
    iconOnly: true,
  }),
  SECONDARY_SELECT: buttons({ size: 'small', color: 'alphaGray', shape: 'rounded' }),
  SECONDARY_PILL_SPECIAL: buttons({ size: 'small', color: 'alphaGray', shape: 'pill' }),
  SECONDARY_SELECT_PILL: buttons({ size: 'small', color: 'white', shape: 'pill' }),
  SECONDARY_MD_ICON: buttons({ size: 'medium', color: 'white', shape: 'pill' }),
  SECONDARY_MD_ICON_ONLY: buttons({
    size: 'medium',
    color: 'white',
    shape: 'pill',
    iconOnly: true,
  }),
  SECONDARY_LIGHT_MD: buttons({ size: 'medium', color: 'white', shape: 'pill' }),
  HEADER_BUTTON: buttons({ size: 'medium', color: 'white', shape: 'pill' }),
  TOKEN_SELECT: buttons({ size: 'small', color: 'gray', shape: 'pill', iconLeft: true }),
  ERROR_LG: buttons({ size: 'large', color: 'red', shape: 'pill' }),
  SUBTLE_SELECT: buttons({ size: 'small', color: 'transparent', shape: 'rounded' }),
  SUBTLE_DEFAULT: buttons({ size: 'small', color: 'transparent', shape: 'rounded' }),
  SUBTLE_DEFAULT_ICON_ONLY: buttons({
    size: 'small',
    color: 'transparent',
    shape: 'rounded',
    iconOnly: true,
  }),
  SUBTLE_DEFAULT_INLINE_ICON_ONLY: buttons({
    size: 'small',
    color: 'transparent',
    shape: 'rounded',
    iconOnly: true,
  }),
  NO_STYLE: '',
};
